/**
 * Exercise React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@services/api";
import { queryKeys } from "@services/queryClient";
import type {
  Exercise,
  ExerciseCreateRequest,
  ExerciseUpdateRequest,
} from "@/types";

interface ExerciseQueryParams {
  workshop_id?: string;
  skip?: number;
  limit?: number;
}

/**
 * Fetch all exercises with optional filters.
 * Use this when you need exercises across multiple workshops (e.g., calculating aggregate stats).
 * For fetching exercises for a single workshop, prefer `useWorkshopExercises` instead.
 */
export function useExercises(params?: ExerciseQueryParams) {
  return useQuery({
    queryKey: params?.workshop_id
      ? queryKeys.exercise.byWorkshop(params.workshop_id)
      : queryKeys.exercise.list(params as Record<string, unknown>),
    queryFn: () =>
      apiGet<Exercise[]>("/exercises", params as Record<string, unknown>),
  });
}

/**
 * Fetch exercises for a specific workshop.
 * Preferred over `useExercises({ workshop_id })` for single-workshop views
 * as it provides cleaner API and automatic query disabling when ID is undefined.
 *
 * @param workshopId - The workshop ID to fetch exercises for. Query is disabled when undefined.
 */
export function useWorkshopExercises(workshopId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.exercise.byWorkshop(workshopId ?? ""),
    queryFn: () =>
      apiGet<Exercise[]>("/exercises", { workshop_id: workshopId }),
    enabled: !!workshopId,
  });
}

/**
 * Fetch a single exercise by ID.
 *
 * @param id - The exercise ID to fetch. Query is disabled when undefined.
 */
export function useExercise(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.exercise.detail(id ?? ""),
    queryFn: () => apiGet<Exercise>(`/exercises/${id}`),
    enabled: !!id,
  });
}

/**
 * Create a new exercise
 */
export function useCreateExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ExerciseCreateRequest) =>
      apiPost<Exercise>("/exercises", data),
    onSuccess: (_, variables) => {
      // Invalidate the workshop's exercise list
      queryClient.invalidateQueries({
        queryKey: queryKeys.exercise.byWorkshop(variables.workshop_id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.exercise.all });
    },
  });
}

/**
 * Update an exercise
 */
export function useUpdateExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: ExerciseUpdateRequest;
      workshopId?: string;
    }) => apiPatch<Exercise>(`/exercises/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.exercise.detail(variables.id),
      });
      // Invalidate workshop exercises if workshopId provided
      if (variables.workshopId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.exercise.byWorkshop(variables.workshopId),
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.exercise.all });
    },
  });
}

/**
 * Delete an exercise
 */
export function useDeleteExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; workshopId?: string }) =>
      apiDelete(`/exercises/${id}`),
    onSuccess: (_, variables) => {
      // Invalidate workshop exercises if workshopId provided
      if (variables.workshopId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.exercise.byWorkshop(variables.workshopId),
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.exercise.all });
    },
  });
}
